import { apiFetch } from "@/integrations/backend/client";

export type ChecklistItem = {
  id: string;
  name: string;
  description: string;
  type: "onboarding" | "lesson" | "assessment";
  conditions: string[]; // List of motivation codes (e.g., "lose-weight", "build-muscle")
  is_conditional: boolean;
  assessment_id: string | null;
  lesson_id: string | null;
  order: number;
  is_deleted?: boolean;
  deleted_at?: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export async function listChecklistItems(includeDeleted?: boolean): Promise<ChecklistItem[]> {
  const url = includeDeleted 
    ? "/api/v1/admin/checklist-items?include_deleted=true"
    : "/api/v1/admin/checklist-items";
  const res = await apiFetch(url, { method: "GET" });
  if (!res.ok) throw new Error(`checklist_items_list_failed:${res.status}`);
  return (await res.json()) as ChecklistItem[];
}

export async function createChecklistItem(input: {
  name: string;
  description: string;
  type: "onboarding" | "lesson" | "assessment";
  is_conditional: boolean;
  conditions: string[];
  assessment_id?: string | null;
  lesson_id?: string | null;
  order?: number;
}): Promise<ChecklistItem> {
  const res = await apiFetch("/api/v1/admin/checklist-items", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`checklist_item_create_failed:${res.status}`);
  return (await res.json()) as ChecklistItem;
}

export async function updateChecklistItem(
  id: string,
  patch: Partial<Pick<ChecklistItem, "name" | "description" | "type" | "is_conditional" | "conditions" | "assessment_id" | "lesson_id" | "order">>
): Promise<ChecklistItem> {
  const res = await apiFetch(`/api/v1/admin/checklist-items/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`checklist_item_update_failed:${res.status}`);
  return (await res.json()) as ChecklistItem;
}

export async function deleteChecklistItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/checklist-items/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`checklist_item_delete_failed:${res.status}`);
}

export async function softDeleteChecklistItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/checklist-items/${id}/soft-delete`, { method: "POST" });
  if (!res.ok) throw new Error(`checklist_item_soft_delete_failed:${res.status}`);
}

export async function reactivateChecklistItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/checklist-items/${id}/reactivate`, { method: "POST" });
  if (!res.ok) throw new Error(`checklist_item_reactivate_failed:${res.status}`);
}

// User checklist items (ChecklistItemToProfile)
export type ChecklistItemToProfile = {
  id: string;
  checklist_item: {
    id: string;
    name: string;
    description: string;
    type: "onboarding" | "lesson" | "assessment";
    order: number;
    assessment_id: string | null;
  };
  is_completed: boolean;
  completed_on: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export async function getMyChecklistItems(): Promise<ChecklistItemToProfile[]> {
  const res = await apiFetch("/api/v1/checklist-items/me", { method: "GET" });
  if (!res.ok) throw new Error(`get_my_checklist_items_failed:${res.status}`);
  return (await res.json()) as ChecklistItemToProfile[];
}

export async function completeChecklistItem(id: string): Promise<ChecklistItemToProfile> {
  const res = await apiFetch(`/api/v1/checklist-items/me/${id}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`complete_checklist_item_failed:${res.status}`);
  return (await res.json()) as ChecklistItemToProfile;
}

// Admin functions for client checklist items
export async function getClientChecklistItems(clientId: string): Promise<ChecklistItemToProfile[]> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/checklist-items`, { method: "GET" });
  if (!res.ok) throw new Error(`get_client_checklist_items_failed:${res.status}`);
  return (await res.json()) as ChecklistItemToProfile[];
}

export async function completeClientChecklistItem(clientId: string, checklistItemToProfileId: string): Promise<ChecklistItemToProfile> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/checklist-items/${checklistItemToProfileId}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`complete_client_checklist_item_failed:${res.status}`);
  return (await res.json()) as ChecklistItemToProfile;
}

export async function incompleteClientChecklistItem(clientId: string, checklistItemToProfileId: string): Promise<ChecklistItemToProfile> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/checklist-items/${checklistItemToProfileId}/incomplete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`incomplete_client_checklist_item_failed:${res.status}`);
  return (await res.json()) as ChecklistItemToProfile;
}

